import { Injectable, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { AccessFolder, Folder, FolderQueryParams } from '@core/models/folder.model';
import { ContentType } from '@core/models/content-type.models';
import { OnDemandActions } from './on-demand.actions';
import {
  selectAllFoldersArray,
  selectAllContentTypesArray,
  selectFoldersPagination,
  selectContentTypeIdsArray,
  selectFolderIdsArray,
  selectSlider,
  selectAllVideoArray,
  selectVideoIdsArray,
  selectVideosPagination,
  selectLoadingVideos,
  selectAccess,
  selectContentTypesPagination,
  selectTotalContentTypes,
} from './on-demand.selectors';
import { VideoData } from '@core/models/entities/video.models';
import { toSignal } from '@angular/core/rxjs-interop';
import { WlcmQueryParams } from '@wlcm/common';

@Injectable({
  providedIn: 'root',
})
export class OnDemandFacade {
  contentTypes$: Observable<ContentType[]>;
  contentTypeEntitiesIds$: Observable<string[]>;
  contentTypesPagination$: Observable<{ totalPages: number; currPage: number; totalItems: number }>;
  contentTypesTotal: Signal<number | null | undefined>;

  accessFolder: Signal<AccessFolder | null | undefined>;

  folders$: Observable<Folder[]>;
  folderEntitiesIds$: Observable<string[]>;
  foldersPagination$: Observable<{ totalPages: number; currentPage: number; totalItems: number }>;

  videos$: Observable<VideoData[]>;
  videoEntitiesIds$: Observable<string[]>;
  videosPagination$: Observable<{ totalPages: number; currentPage: number; totalItems: number }>;

  loadingVideos: Signal<boolean | undefined>;

  slider$: Observable<{
    isShowAll: boolean;
    sliderRange: number;
  }>;

  constructor(private store: Store) {
    this.contentTypes$ = this.store.select(selectAllContentTypesArray);
    this.contentTypeEntitiesIds$ = this.store.select(selectContentTypeIdsArray);
    this.contentTypesPagination$ = this.store.select(selectContentTypesPagination);
    this.contentTypesTotal = toSignal(this.store.select(selectTotalContentTypes));

    this.folders$ = this.store.select(selectAllFoldersArray);
    this.folderEntitiesIds$ = this.store.select(selectFolderIdsArray);
    this.foldersPagination$ = this.store.select(selectFoldersPagination);

    this.videos$ = this.store.select(selectAllVideoArray);
    this.videoEntitiesIds$ = this.store.select(selectVideoIdsArray);
    this.videosPagination$ = this.store.select(selectVideosPagination);

    this.loadingVideos = toSignal(this.store.select(selectLoadingVideos));

    this.slider$ = this.store.select(selectSlider);

    this.accessFolder = toSignal(this.store.select(selectAccess));
  }

  initContentType(queryParams: WlcmQueryParams): void {
    this.store.dispatch(OnDemandActions.getContentTypes(queryParams));
  }

  loadContentType(queryParams: WlcmQueryParams): void {
    this.store.dispatch(OnDemandActions.getContentTypes(queryParams));
  }

  initCollections(queryParams: FolderQueryParams): void {
    this.store.dispatch(OnDemandActions.initCollections(queryParams));
  }

  loadMoreCollections(queryParams: FolderQueryParams): void {
    this.store.dispatch(OnDemandActions.getCollections(queryParams));
  }

  clearCollections(): void {
    this.store.dispatch(OnDemandActions.clearCollections());
  }

  loadMoreVideos(queryParams: FolderQueryParams): void {
    this.store.dispatch(OnDemandActions.getVideos(queryParams));
  }

  clearVideoState(): void {
    this.store.dispatch(OnDemandActions.clearVideos());
  }

  updateSliderStatus(): void {
    this.store.dispatch(OnDemandActions.updateSliderStatus());
  }
  updateSliderRange(range: number): void {
    this.store.dispatch(OnDemandActions.updateSliderRange({ range }));
  }

  updateLoading(status: boolean): void {
    this.store.dispatch(OnDemandActions.loadingVideos({ isLoading: status }));
  }
}
